import type { TrackPoint } from "@/lib/client-convert";

// A user-selected time window (dragged directly on the Performance chart) to
// replace with a straight line between its own boundary readings.
export type HrFixRange = { startMs: number; endMs: number };

// Replace heart rate readings across each user-selected range with a linear
// interpolation between the range's own boundary readings — a manual
// alternative to guessing at automatic dropout detection, for glitches the
// user can see and select directly on the chart. A range whose boundary
// reading is itself missing is skipped: there's nothing to draw a line from.
export function applyHrFixRanges(
  points: TrackPoint[],
  ranges: HrFixRange[]
): { points: TrackPoint[]; fixedCount: number } {
  if (!ranges.length) return { points, fixedCount: 0 };

  const out = points.slice();
  let fixedCount = 0;

  for (const { startMs, endMs } of ranges) {
    const startIndex = out.findIndex((p) => p.timeMs >= startMs);
    let endIndex = -1;
    for (let i = out.length - 1; i >= 0; i -= 1) {
      if (out[i].timeMs <= endMs) {
        endIndex = i;
        break;
      }
    }
    if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) continue;

    const before = out[startIndex];
    const after = out[endIndex];
    if (before.hr === undefined || after.hr === undefined) continue;

    const hrBefore = before.hr;
    const hrAfter = after.hr;
    const spanMs = after.timeMs - before.timeMs;
    for (let i = startIndex; i <= endIndex; i += 1) {
      const t = spanMs > 0 ? (out[i].timeMs - before.timeMs) / spanMs : 0;
      out[i] = { ...out[i], hr: Math.round(hrBefore + (hrAfter - hrBefore) * t) };
      fixedCount += 1;
    }
  }

  return { points: out, fixedCount };
}
